const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "";

  // Already display-formatted (e.g. 15.07.2026)
  if (String(dateStr).includes(".")) {
    return dateStr;
  }

  const parts = String(dateStr).split("-");
  if (parts.length === 3) {
    return `${parts[2]}.${parts[1]}.${parts[0]}`;
  }

  return dateStr;
};

export const formatHistoricalListDate = (dateStr) => {
  if (!dateStr) return "";

  // Convert DD.MM.YYYY → YYYY-MM-DD for Date parsing
  let normalized = dateStr;
  if (String(dateStr).includes(".")) {
    const [dd, mm, yyyy] = String(dateStr).split(".");
    if (dd && mm && yyyy) {
      normalized = `${yyyy}-${mm}-${dd}`;
    }
  }

  const date = new Date(`${normalized}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return dateStr;
  }

  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export const formatPulseDuration = (duration, audios = []) => {
  if (duration) {
    if (duration.includes(":")) {
      const [minutes] = duration.split(":");
      const parsedMinutes = parseInt(minutes, 10);
      if (!Number.isNaN(parsedMinutes)) {
        return `${parsedMinutes} min`;
      }
    }
    return duration;
  }

  const firstAudioDuration = audios[0]?.duration;
  if (firstAudioDuration) {
    return formatPulseDuration(firstAudioDuration);
  }

  return "";
};

const getImageUrl = (image = {}) => image.image_url || image.url || "";

const normalizeImages = (item = {}) => {
  if (Array.isArray(item.images)) {
    return item.images
      .filter((image) => getImageUrl(image))
      .sort((left, right) => (left.sort_order || 0) - (right.sort_order || 0))
      .map((image, index) => ({
        id: image.id || `img-${index}`,
        url: getImageUrl(image),
        title: image.title || image.label || item.topic || item.market || "",
        sortOrder: image.sort_order || index,
      }));
  }

  if (item.chart_image) {
    return [{ url: item.chart_image }];
  }

  return [];
};

const normalizeAudios = (item = {}) => {
  if (Array.isArray(item.audios)) {
    return item.audios
      .filter((audio) => audio?.url)
      .map((audio) => ({
        url: audio.url,
        duration: audio.duration || "",
      }));
  }

  if (Array.isArray(item.audio)) {
    return item.audio
      .filter((audio) => audio?.url)
      .map((audio) => ({
        url: audio.url,
        duration: audio.duration || "",
      }));
  }

  if (item.audio_url) {
    return [{ url: item.audio_url, duration: item.duration || "" }];
  }

  return [];
};

export const getExtraImageCount = (item = {}) => {
  const imageCount = normalizeImages(item).length;
  return imageCount > 1 ? imageCount - 1 : 0;
};

export const getExtraMediaCount = (item = {}) => getExtraImageCount(item);

const getPulseDate = (item = {}) =>
  item.displayDate ||
  item.publish_date ||
  item.date ||
  item.rawDate ||
  "";

/**
 * Unwrap axios interceptor payload (already response.data) or raw envelope.
 */
const getEnvelope = (response) => {
  if (!response || typeof response !== "object") return null;
  if (response.data != null || response.status != null || response.pagination) {
    return response;
  }
  return null;
};

export const extractAnalystProfile = (response) => {
  const envelope = getEnvelope(response);
  const analyst =
    envelope?.data?.analystData ||
    envelope?.analystData ||
    null;

  if (!analyst) return null;

  return {
    name: analyst.name || "",
    designation: analyst.title || analyst.designation || "",
    avatarUrl: analyst.avatarUrl || analyst.avatar_url || "",
    bio: analyst.profile || analyst.bio || "",
  };
};

/**
 * Flatten `marketPulseData: { data1: {...}, data2: {...} }` into pulse items.
 */
const flattenMarketPulseData = (
  marketPulseData,
  { displayDate = "", author = "" } = {},
) => {
  if (!marketPulseData || typeof marketPulseData !== "object") {
    return [];
  }

  return Object.entries(marketPulseData)
    .filter(([, value]) => value && typeof value === "object")
    .map(([key, value]) => ({
      id: key,
      topic: value.topic || "",
      market: value.topic || value.market || "",
      title: value.topic || value.title || value.market || "",
      description: value.description || "",
      session: value.session || "Pre-Market",
      displayDate,
      date: displayDate,
      author: author || value.author || "",
      audios: value.audios || [],
      images: value.images || [],
      duration: value.duration || value.audios?.[0]?.duration || "",
    }));
};

export const normalizePulseItem = (item = {}) => {
  const audios = normalizeAudios(item);
  const images = normalizeImages(item);
  const pulseDate = getPulseDate(item);

  return {
    id: item.id,
    market: item.market || item.topic || item.title || "",
    title: item.title || item.topic || item.market || "",
    session: item.session || "",
    date: formatDisplayDate(pulseDate),
    listDate: formatHistoricalListDate(pulseDate),
    rawDate: pulseDate,
    description: item.description || "",
    duration: formatPulseDuration(item.duration, audios),
    extraMediaCount: getExtraImageCount(item),
    author: item.author || "",
    audios,
    images,
    raw: item,
  };
};

const dayObjectToPulses = (day) => {
  if (!day || typeof day !== "object") return [];

  const displayDate = day.displayDate || day.date || "";
  const author = day.analystData?.name || day.author || "";

  if (day.marketPulseData) {
    return flattenMarketPulseData(day.marketPulseData, {
      displayDate,
      author,
    }).map(normalizePulseItem);
  }

  // Single pulse-shaped day
  if (day.topic || day.market || day.description || day.audios || day.images) {
    return [
      normalizePulseItem({
        ...day,
        displayDate,
        author,
      }),
    ];
  }

  return [];
};

/**
 * Current: data = { displayDate, analystData, marketPulseData }
 * Also supports legacy: data = [] | { market_pulses: [] }
 */
export const normalizeMarketPulseResponse = (response) => {
  const envelope = getEnvelope(response);
  if (!envelope) return [];

  const data = envelope.data;

  if (data && !Array.isArray(data) && data.marketPulseData) {
    return dayObjectToPulses(data).filter(
      (pulse) =>
        pulse.title ||
        pulse.market ||
        pulse.description ||
        pulse.audios.length > 0 ||
        pulse.images.length > 0,
    );
  }

  const items = Array.isArray(data?.market_pulses)
    ? data.market_pulses
    : Array.isArray(data)
      ? data
      : [];

  return items
    .flatMap((item) => {
      if (item?.marketPulseData) {
        return dayObjectToPulses(item);
      }
      return [normalizePulseItem(item)];
    })
    .filter(
      (pulse) =>
        pulse.title ||
        pulse.market ||
        pulse.description ||
        pulse.audios.length > 0 ||
        pulse.images.length > 0,
    );
};

/**
 * Historical: data = [] (day or pulse items) + pagination { hasMore, nextCursor, windowSize }
 */
export const normalizeHistoricalMarketPulseResponse = (response) => {
  const envelope = getEnvelope(response);
  const data = envelope?.data;
  const pagination = envelope?.pagination || {};

  let pulses = [];

  if (Array.isArray(data)) {
    pulses = data.flatMap((item) => {
      if (item?.marketPulseData) {
        return dayObjectToPulses(item);
      }
      return [normalizePulseItem(item)];
    });
  } else if (data && typeof data === "object" && data.marketPulseData) {
    pulses = dayObjectToPulses(data);
  } else if (Array.isArray(data?.market_pulses)) {
    pulses = data.market_pulses.map(normalizePulseItem);
  }

  pulses = pulses.filter(
    (pulse) =>
      pulse.title ||
      pulse.market ||
      pulse.description ||
      pulse.audios.length > 0 ||
      pulse.images.length > 0,
  );

  return {
    pulses,
    pagination: {
      hasMore: Boolean(pagination.hasMore),
      nextCursor:
        pagination.nextCursor ||
        pagination.next_cursor ||
        pagination.nextDate ||
        pagination.next_date ||
        null,
      windowSize: pagination.windowSize || pagination.window_size || 10,
    },
  };
};

export const buildPulseSubtitle = (pulses = []) => {
  const markets = [
    ...new Set(
      pulses.map((pulse) => pulse.market || pulse.title).filter(Boolean),
    ),
  ];
  return markets.length
    ? markets.join(" | ")
    : "Nifty | Bank Nifty | Key Levels";
};

export const getPulseImageCardTitle = (pulse, image, index) => {
  if (image?.title) {
    return image.title;
  }

  if (pulse.images.length === 1) {
    return pulse.title || pulse.market || "Market Pulse";
  }

  const fallbackTitles = ["Nifty 50", "Bank Nifty", "Chart"];
  return fallbackTitles[index] || `${pulse.title || "Chart"} ${index + 1}`;
};
